import * as proto from 'src/proto/compiled-protos';
import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';
import { LoadableContentComponent } from 'src/app/general/components/loadable-content/loadable-content.component';
import { BackendService } from '../../services/backend.service';
import { CompactTextColumn, DataTableColumn, StringColumn } from 'src/app/general/components/data-table/data-table-column';
import { ComponentUtil } from 'src/app/general/util/component-util';
import { DataTableComponent } from 'src/app/general/components/data-table/data-table.component';
import { LoadingController } from 'src/app/general/components/loadable-content/loading-controler';
import { ErrorResult, LoadingMessage } from 'src/app/general/util/result';
import { IndeterminatePaginatorComponent } from 'src/app/general/components/indeterminate-paginator/indeterminate-paginator.component';
import { IndeterminatePaginatorData, IndeterminatePaginatorModel } from 'src/app/general/components/indeterminate-paginator/indeterminate-paginator-model';
import { map, Observable, of } from 'rxjs';
import { Util } from 'src/app/general/util/util';

// ----------------------------------------------------------------------------
// Usage Example

// Template:

// <app-top-locations year="{{year}}"></app-top-locations>
// <app-top-locations year="{{year}}" month="{{month}}"></app-top-locations>
// <app-top-locations year="{{year}}" month="{{month}}" day="{{day}}"></app-top-locations>
// ----------------------------------------------------------------------------

@Component({
  selector: 'app-top-locations',
  standalone: true,
  imports: [
    CommonModule,
    LoadableContentComponent,
    TranslateModule,
    MatCardModule,
    DataTableComponent,
    IndeterminatePaginatorComponent
  ],
  templateUrl: './top-locations.component.html',
  styleUrls: ['./top-locations.component.css']
})
export class TopLocationsComponent implements IndeterminatePaginatorModel<proto.waiternow.common.ILocationMetricProto>, OnInit, OnChanges {
  @Input() year!: number;
  @Input() month?: proto.waiternow.common.Month;
  @Input() day?: number;

  loadingController: LoadingController;

  topLocations: Array<proto.waiternow.common.ILocationMetricProto>;
  columns: Array<DataTableColumn<proto.waiternow.common.ILocationMetricProto>>;

  initialPaginatorData: IndeterminatePaginatorData<proto.waiternow.common.ILocationMetricProto>;
  paginatorModel: IndeterminatePaginatorModel<proto.waiternow.common.ILocationMetricProto>;

  constructor(
      private backendService: BackendService,
      private changeDetectorRef: ChangeDetectorRef) {
    this.loadingController = new LoadingController();

    this.topLocations = new Array();
    this.columns = [
      new CompactTextColumn(
        /* name= */ 'id',
        /* translateId= */ 'id',
        /* valueExtractor= */  locationMetric => locationMetric.locationId,
        /* compactTextLength= */ 6),
      new StringColumn(
        /* name= */ 'location',
        /* translateId= */ 'location',
        /* valueExtractor= */  locationMetric => Util.safeString(locationMetric.redundantData?.businessName) + ' ' + Util.safeString(locationMetric.redundantData?.locationName)),
      new StringColumn(
        /* name= */ 'paidOrders',
        /* translateId= */ 'paid_orders',
        /* valueExtractor= */  locationMetric => Util.safeNumber(locationMetric.paidOrders).toString()),
      ];

      this.initialPaginatorData = { data: new Array(), continuationToken: ''};
      this.paginatorModel = this;
  }

  ngOnInit(): void {
    this.loadInitialData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (ComponentUtil.bindingChanged('year', changes)
        || ComponentUtil.bindingChanged('month', changes)
        || ComponentUtil.bindingChanged('day', changes)) {
      this.ngOnInit();
    }
  }

  updatePage(page: proto.waiternow.common.ILocationMetricProto[]): void {
    this.topLocations = page;
    // This is needed because the update may be done async after the result of a backend call, and thus after Angular change detection process.
    this.changeDetectorRef.detectChanges();
  }

  fetchData(continuationToken: string | null | undefined): Observable<IndeterminatePaginatorData<proto.waiternow.common.ILocationMetricProto>> {
    if (!this.year && ! this.month && !this.day) {
      return of({data: [], continuationToken: ''});
    }

    return this.backendService.findTopLocationsReturnObservable(this.year, this.month, this.day, continuationToken)
      .pipe(
        map(locationMetrics  => {
          const paginatorData: IndeterminatePaginatorData<proto.waiternow.common.ILocationMetricProto> = {data: [], continuationToken: continuationToken};
          if (locationMetrics) {
            paginatorData.data = locationMetrics.metrics;
            paginatorData.continuationToken = locationMetrics.continuationToken;
          }
          return paginatorData;
        })
      );
  }

  public loadInitialData(): void {
    this.loadingController.onLoadingStarted(LoadingMessage.withMessageTranslateId('loading'));
    this.fetchData(/* continuationToken= */ null).subscribe(
      {
        next: paginatorData => {
          this.initialPaginatorData = paginatorData;
          this.loadingController.onSuccess();
          // This is needed because the update may be done async after the result of a backend call, and thus after Angular change detection process.
          this.changeDetectorRef.detectChanges();
        },
        error: error => {
          this.loadingController.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_loading'));
        }
      }
    );
  }

  public getDate(): string {
    if (this.year && this.month && this.day) {
      return `${this.year}-${this.pad(this.month)}-${this.pad(this.day)}`;
    } else if (this.year && this.month) {
      return `${this.year}-${this.pad(this.month)}`;
    } else if (this.year) {
      return `${this.year}`;
    }
    return '';
  }

  private pad(dateComponent: number): string {
    return dateComponent.toString().padStart(2, '0');
  }
}
